/**
 * ColumnMappingModal Component
 * Reusable modal for mapping data columns to expected fields
 * Used in: GeoLift, Power Calculator, and other data upload features
 */

import React, { useState, useEffect } from "react";
import {
  Modal,
  Select,
  Alert,
  Typography,
  Space,
  Tag,
  Button,
  Divider,
} from "antd";
import {
  CheckCircleOutlined,
  WarningOutlined,
  InfoCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import type { ColumnMapping, ValidationResult } from "../utils/dataValidation";
import { validateRequiredMappings } from "../utils/dataValidation";

const { Text, Title } = Typography;
const { Option } = Select;

interface ColumnMappingModalProps {
  /**
   * Whether the modal is visible
   */
  visible: boolean;

  /**
   * Validation result containing detected mappings and available columns
   */
  validationResult: ValidationResult;

  /**
   * Title of the modal
   */
  title?: string;

  /**
   * Callback when user confirms the mappings
   * Returns the updated mappings
   */
  onConfirm: (mappings: ColumnMapping[]) => void;

  /**
   * Callback when user cancels
   */
  onCancel: () => void;

  /**
   * Loading state for confirm button
   */
  confirmLoading?: boolean;

  /**
   * Custom confirm button text
   */
  confirmText?: string;

  /**
   * Whether to show dropdowns in edit mode initially (defaults to auto-detect based on confidence)
   */
  initialEditMode?: boolean;
}

const ColumnMappingModal: React.FC<ColumnMappingModalProps> = ({
  visible,
  validationResult,
  title = "Map Data Columns",
  onConfirm,
  onCancel,
  confirmLoading = false,
  confirmText = "Confirm Mappings",
  initialEditMode = false,
}) => {
  const [mappings, setMappings] = useState<ColumnMapping[]>(
    validationResult.mappings
  );
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [isEditMode, setIsEditMode] = useState<boolean>(initialEditMode);

  // Update mappings when validationResult changes
  useEffect(() => {
    setMappings(validationResult.mappings);
    // Always start in edit mode to show users what was mapped
    // They can collapse it after reviewing
    setIsEditMode(true);
  }, [validationResult, initialEditMode]);

  // Validate mappings whenever they change
  useEffect(() => {
    const validation = validateRequiredMappings(mappings);
    setValidationErrors(validation.errors);
  }, [mappings]);

  const handleColumnChange = (
    fieldType: string,
    selectedColumn: string | null
  ) => {
    setMappings((prevMappings) =>
      prevMappings.map((mapping) =>
        mapping.field === fieldType
          ? {
              ...mapping,
              detectedColumn: selectedColumn,
              confidence: selectedColumn ? 1.0 : 0,
            }
          : mapping
      )
    );
  };

  const handleConfirm = () => {
    const validation = validateRequiredMappings(mappings);
    if (!validation.isValid) {
      setValidationErrors(validation.errors);
      return;
    }
    onConfirm(mappings);
  };

  const getConfidenceColor = (confidence: number): string => {
    if (confidence >= 0.8) return "success";
    if (confidence >= 0.5) return "warning";
    return "error";
  };

  const getConfidenceIcon = (confidence: number) => {
    if (confidence >= 0.8) return <CheckCircleOutlined />;
    if (confidence >= 0.5) return <WarningOutlined />;
    return <CloseCircleOutlined />;
  };

  const isColumnUsed = (column: string, currentField: string): boolean => {
    const usedColumns = mappings
      .filter((m) => m.field !== currentField && m.detectedColumn !== null)
      .map((m) => m.detectedColumn as string);
    return usedColumns.includes(column);
  };

  // Generate intelligent description based on mapping status
  const generateMappingDescription = (): {
    message: string;
    type: "success" | "warning" | "info";
  } => {
    const requiredMappings = mappings.filter((m) => m.required);
    const mappedRequired = requiredMappings.filter((m) => m.detectedColumn);
    const missingRequired = requiredMappings.filter((m) => !m.detectedColumn);
    const highConfidence = mappings.filter(
      (m) => m.detectedColumn && m.confidence >= 0.8
    );

    if (missingRequired.length > 0) {
      const missingFields = missingRequired.map((m) => m.label).join(", ");
      return {
        message: `Critical: ${missingRequired.length} required field${
          missingRequired.length > 1 ? "s are" : " is"
        } missing (${missingFields}). Please map these columns to proceed with GeoLift analysis.`,
        type: "warning",
      };
    }

    if (highConfidence.length === requiredMappings.length) {
      return {
        message: `We've automatically mapped ${
          mappedRequired.length
        } required field${
          mappedRequired.length > 1 ? "s" : ""
        } with high confidence. Review the mappings below and click "Edit" if you need to make changes.`,
        type: "success",
      };
    }

    return {
      message: `We've mapped ${mappedRequired.length}/${requiredMappings.length} required fields using smart detection logic. Please review and update any mappings if needed.`,
      type: "info",
    };
  };

  const mappingDescription = generateMappingDescription();

  return (
    <Modal
      title={
        <Space>
          <InfoCircleOutlined className="text-blue-500" />
          <span>{title}</span>
        </Space>
      }
      open={visible}
      onCancel={onCancel}
      width={700}
      confirmLoading={confirmLoading}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Cancel
        </Button>,
        <Button
          key="confirm"
          type="primary"
          onClick={handleConfirm}
          loading={confirmLoading}
          disabled={validationErrors.length > 0}
        >
          {confirmText}
        </Button>,
      ]}
    >
      <div className="space-y-4">
        {/* Smart mapping description */}
        <Alert
          message={mappingDescription.message}
          type={mappingDescription.type}
          showIcon
          action={
            !isEditMode && mappingDescription.type === "success" ? (
              <Button
                size="small"
                type="link"
                onClick={() => setIsEditMode(true)}
              >
                Edit
              </Button>
            ) : null
          }
        />

        {/* Validation errors */}
        {validationErrors.length > 0 && (
          <Alert
            message="Please fix the following issues:"
            description={
              <ul className="list-disc pl-4 mb-0">
                {validationErrors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            }
            type="error"
            showIcon
          />
        )}

        {/* Column mappings - only show if in edit mode or has issues */}
        {isEditMode && (
          <>
            <Divider className="my-4" />
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-3">
                <Title level={5} className="mb-0">
                  Column Mappings
                </Title>
                <Button
                  type="text"
                  size="small"
                  onClick={() => setIsEditMode(false)}
                >
                  Collapse
                </Button>
              </div>

              {mappings.map((mapping) => (
                <div
                  key={mapping.field}
                  className={`p-4 rounded-lg border ${
                    mapping.required
                      ? "border-blue-200 bg-blue-50"
                      : "border-gray-200 bg-gray-50"
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <Space>
                        <Text strong className="text-base">
                          {mapping.label}
                        </Text>
                        {mapping.required ? (
                          <Tag color="red">Required</Tag>
                        ) : (
                          <Tag color="default">Optional</Tag>
                        )}
                        {mapping.detectedColumn && (
                          <Tag
                            color={getConfidenceColor(mapping.confidence)}
                            icon={getConfidenceIcon(mapping.confidence)}
                          >
                            {mapping.confidence >= 0.8
                              ? "Matched"
                              : mapping.confidence >= 0.5
                              ? "Medium confidence"
                              : "Low confidence"}
                          </Tag>
                        )}
                      </Space>
                      <div className="mt-1">
                        <Text type="secondary" className="text-sm">
                          {mapping.description}
                        </Text>
                      </div>
                    </div>
                  </div>

                  <Select
                    style={{ width: "100%" }}
                    placeholder={`Select column for ${mapping.label}`}
                    value={mapping.detectedColumn}
                    onChange={(value) =>
                      handleColumnChange(mapping.field, value)
                    }
                    allowClear
                    showSearch
                    optionFilterProp="children"
                  >
                    {validationResult.availableColumns.map((column) => (
                      <Option
                        key={column}
                        value={column}
                        disabled={isColumnUsed(column, mapping.field)}
                      >
                        <Space>
                          <span>{column}</span>
                          {isColumnUsed(column, mapping.field) && (
                            <Tag color="default" className="text-xs">
                              Already mapped
                            </Tag>
                          )}
                        </Space>
                      </Option>
                    ))}
                  </Select>
                </div>
              ))}
            </div>

            {/* Summary of available columns */}
            <Divider className="my-4" />
            <div className="bg-gray-50 p-3 rounded-lg">
              <Text type="secondary" className="text-sm">
                <strong>
                  Available Columns ({validationResult.availableColumns.length}
                  ):
                </strong>{" "}
                {validationResult.availableColumns.join(", ")}
              </Text>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default ColumnMappingModal;
